import { useEffect, useMemo, useState } from "react";
import "./CarDetailsPage.css";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import Skeleton from "@mui/material/Skeleton";
import {
    ChevronLeft, ChevronRight, Copy, ArrowLeft, Download, GeoAlt, CarFront,
    CashCoin, TruckFront, PersonBadge, JournalText, Key, Lightning, CheckCircleFill, Image as ImageIcon,
    Trash, PlusLg, Wallet2, Tag, ExclamationTriangleFill
} from "react-bootstrap-icons";
import core from "../../core";
import { notificationActions } from "../../data/notification";
import JSZip from "jszip";
import useT from "../../i18n/useT";

/* stored image URLs may be absolute (legacy, wrong host) or already parsed arrays
   (mysql2 auto-parses JSON columns) — normalise both to same-origin /static paths */
const normalizeImg = (u) => {
    if (typeof u !== "string") return u;
    const i = u.indexOf("/static");
    return i >= 0 ? u.slice(i) : u;
};
const parseImgs = (val) => {
    if (!val) return [];
    let arr = val;
    if (typeof val === "string") { try { arr = JSON.parse(val); } catch { arr = []; } }
    if (!Array.isArray(arr)) return [];
    return arr.map(normalizeImg).filter(Boolean);
};
const money = (n) => (n || n === 0) && !isNaN(Number(n)) ? "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—";
const fmtDate = (d) => (d && d !== "0000-00-00" && !String(d).startsWith("1970")) ? core.formdatDate(d, "dd-mm-yyyy") : null;

const copyText = (text) => {
    if (!text) return;
    if (navigator.clipboard) { navigator.clipboard.writeText(text).catch(() => {}); return; }
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); } catch {}
    document.body.removeChild(ta);
};

const IMG_TYPES = [
    { key: "auction_images", label: "Auction", flag: "has_auction_image" },
    { key: "transportation_images", label: "Transportation", flag: "has_transportation_image" },
    { key: "port_images", label: "Port", flag: "has_port_image" },
    { key: "poti_images", label: "Poti", flag: "has_poti_image" },
];

const InfoRow = ({ label, value, copy, mono }) => (
    <div className="cd-row">
        <span className="cd-row-label">{label}</span>
        <span className={`cd-row-value${mono ? " mono" : ""}`}>
            {value === null || value === undefined || value === "" ? "—" : value}
            {copy && value ? <Copy className="cd-copy" onClick={() => copyText(value)} /> : null}
        </span>
    </div>
);

const Section = ({ icon, title, children, className = "" }) => (
    <div className={`cd-card ${className}`}>
        <h3 className="cd-card-title">{icon}{title}</h3>
        {children}
    </div>
);

// Logistics stages in order. `done` = the stage's date (or flag) is set.
// The ETA stage shows the estimated arrival even before it's reached.
const buildStages = (v, t) => {
    const stages = [
        { key: "auction", label: t("cd.stage_auction"), date: v.purchase_date },
        { key: "pickup", label: t("cd.stage_pickup"), date: v.vehicle_pickup_date },
        { key: "warehouse", label: t("cd.stage_warehouse"), date: v.warehouse_receive_date },
        { key: "loaded", label: t("cd.stage_loaded"), date: v.container_loading_date },
        { key: "transit", label: t("cd.stage_transit"), date: v.estimated_receive_date, isEta: true },
        { key: "arrived", label: t("cd.stage_arrived"), date: v.receive_date || v.container_receive_date },
        { key: "opened", label: t("cd.stage_opened"), date: v.container_open_date },
        { key: "cleared", label: t("cd.stage_cleared"), date: v.is_green == 1 ? true : null, flag: true },
    ];
    return stages.map((s) => ({ ...s, done: s.flag ? !!s.date : (s.date && String(s.date) !== "0000-00-00" && !String(s.date).startsWith("1970")) }));
};

const Timeline = ({ vehicle, t }) => {
    const stages = buildStages(vehicle, t);
    const lastDone = stages.reduce((acc, s, i) => (s.done ? i : acc), -1);
    const fmt = (d) => (d && d !== true && d !== "0000-00-00" && !String(d).startsWith("1970")) ? core.formdatDate(d, "dd-mm-yyyy") : null;

    return (
        <div className="cd-ship-tl">
            {stages.map((s, i) => {
                const state = s.done ? "done" : (i === lastDone + 1 ? "current" : "pending");
                return (
                    <div key={s.key} className={`cd-tl-step ${state}`}>
                        <div className="cd-tl-dot">{s.done ? <CheckCircleFill /> : i + 1}</div>
                        <div className="cd-tl-label">{s.label}</div>
                        <div className="cd-tl-date">
                            {s.isEta && !s.done && s.date ? <span className="cd-tl-eta">ETA {fmt(s.date)}</span> : (fmt(s.date) || "—")}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const COST_CATEGORIES = ["auction", "transport", "insurance", "service", "other"];

const CostForm = ({ t, onAdd }) => {
    const [form, setForm] = useState({ category: "auction", amount: "", description: "", cost_date: "" });
    const [saving, setSaving] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        if (form.amount === "" || isNaN(Number(form.amount))) return;
        setSaving(true);
        Promise.resolve(onAdd(form)).finally(() => {
            setSaving(false);
            setForm({ category: form.category, amount: "", description: "", cost_date: "" });
        });
    };

    return (
        <form className="cd-cost-form" onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "flex-end", marginTop: "12px" }}>
            <select className="form-control" style={{ flex: "1 1 130px" }} value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                {COST_CATEGORIES.map((c) => <option key={c} value={c}>{t(`cd.cost_${c}`)}</option>)}
            </select>
            <input className="form-control" style={{ flex: "1 1 100px" }} type="number" step="0.01" placeholder={t("cd.costAmount")} value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} required />
            <input className="form-control" style={{ flex: "2 1 160px" }} type="text" placeholder={t("cd.costDescription")} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            <input className="form-control" style={{ flex: "1 1 130px" }} type="date" value={form.cost_date} onChange={(e) => setForm((p) => ({ ...p, cost_date: e.target.value }))} />
            <button type="submit" className="btn btn-primary" disabled={saving}><PlusLg /> {t("cd.costAdd")}</button>
        </form>
    );
};

const DiscountForm = ({ t, onAdd }) => {
    const [form, setForm] = useState({ amount: "", reason: "" });
    const [saving, setSaving] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        if (form.amount === "" || Number(form.amount) <= 0) return;
        setSaving(true);
        Promise.resolve(onAdd(form)).finally(() => {
            setSaving(false);
            setForm({ amount: "", reason: "" });
        });
    };

    return (
        <form className="cd-cost-form" onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "flex-end", marginTop: "12px" }}>
            <input className="form-control" style={{ flex: "1 1 100px" }} type="number" step="0.01" min="0" placeholder={t("cd.discountAmount")} value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} required />
            <input className="form-control" style={{ flex: "2 1 180px" }} type="text" placeholder={t("cd.discountReason")} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} />
            <button type="submit" className="btn btn-primary" disabled={saving}><PlusLg /> {t("cd.discountAdd")}</button>
        </form>
    );
};

const CarDetailsPage = () => {
    const id = window.location.pathname.replace("/vehicle-details/", "").replaceAll("/", "");
    const [vehicle, setVehicle] = useState(null);
    const [notes, setNotes] = useState([]);
    const [booking, setBooking] = useState(null);
    const [imagesByType, setImagesByType] = useState({});
    const [imagesType, setImagesType] = useState(null);
    const [imageIndex, setImageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [costs, setCosts] = useState([]);
    const [costSummary, setCostSummary] = useState(null);
    const [discounts, setDiscounts] = useState([]);
    const [discountTotal, setDiscountTotal] = useState(0);
    const [customsFine, setCustomsFine] = useState(null);
    const [updates, setUpdates] = useState(0);
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const canEditCosts = role === "admin" || role === "moderator";

    useEffect(() => {
        axios(`/api/vehicle/${id}`)
            .then((res) => {
                const data = res.data.data;
                setVehicle(data.vehicle);
                setNotes(data.notes || []);
                setBooking((data.booking && data.booking[0]) || null);
                setCosts(data.costs || []);
                setCostSummary(data.costSummary || null);
                setDiscounts(data.discounts || []);
                setDiscountTotal(data.discountTotal || 0);
                setCustomsFine(data.customsFine || null);
                const imgRow = (data.images && data.images[0]) || {};
                const byType = {};
                IMG_TYPES.forEach((t) => { byType[t.key] = parseImgs(imgRow[t.key]); });
                setImagesByType(byType);
                const firstAvailable = IMG_TYPES.find((t) => byType[t.key].length > 0);
                setImagesType(firstAvailable ? firstAvailable.key : null);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [id, updates]);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    const addCost = (payload) => {
        return axios.post(`/api/vehicle/${id}/costs`, payload)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => { notify(err.response?.data?.message?.en || "Error", "error-notification"); });
    };

    const deleteCost = (costId) => {
        axios.delete(`/api/vehicle/${id}/costs/${costId}`)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => { notify(err.response?.data?.message?.en || "Error", "error-notification"); });
    };

    const addDiscount = (payload) => {
        return axios.post(`/api/vehicle/${id}/discounts`, payload)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => { notify(err.response?.data?.message?.en || "Error", "error-notification"); });
    };

    const deleteDiscount = (discountId) => {
        axios.delete(`/api/vehicle/${id}/discounts/${discountId}`)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => { notify(err.response?.data?.message?.en || "Error", "error-notification"); });
    };

    const waiveCustomsFine = () => {
        axios.delete(`/api/vehicle/${id}/customs-fine`)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => { notify(err.response?.data?.message?.en || "Error", "error-notification"); });
    };

    const images = (imagesType && imagesByType[imagesType]) || [];
    const availableTypes = IMG_TYPES.filter((t) => (imagesByType[t.key] || []).length > 0);

    const downloadImages = async () => {
        const zip = new JSZip();
        const folder = zip.folder("images");
        for (let i = 0; i < images.length; i++) {
            try {
                const response = await fetch(images[i]);
                const blob = await response.blob();
                const ext = images[i].split(".").pop().split(/#|\?/)[0];
                folder.file(`image-${i + 1}.${ext}`, blob);
            } catch (e) { /* skip */ }
        }
        const content = await zip.generateAsync({ type: "blob" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(content);
        link.download = `${vehicle ? vehicle.vin : "vehicle"}-images.zip`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    // ---- payment summary ----
    const pay = useMemo(() => {
        if (!vehicle) return { paid: 0, debt: 0, total: 0, pct: 0 };
        const paid = Number(vehicle.payed_amount) || 0;
        const debt = Number(vehicle.debt_amount) || 0;
        const total = paid + debt;
        return { paid, debt, total, pct: total > 0 ? (paid / total) * 100 : (vehicle.is_fully_paid ? 100 : 0) };
    }, [vehicle]);

    const payStatus = vehicle && (vehicle.is_fully_paid == 1
        ? { label: t("cd.fullyPaid"), cls: "ok" }
        : vehicle.is_partially_paid == 1 ? { label: t("cd.partiallyPaid"), cls: "warn" } : { label: t("cd.unpaid"), cls: "danger" });

    // ---- journey timeline ----
    const timeline = vehicle ? [
        { label: t("cd.purchased"), date: vehicle.purchase_date },
        { label: t("cd.atWarehouse"), date: vehicle.warehouse_receive_date },
        { label: t("cd.pickedUp"), date: vehicle.vehicle_pickup_date },
        { label: t("cd.containerLoaded"), date: vehicle.container_loading_date },
        { label: t("cd.arrivedAtPort"), date: vehicle.container_receive_date || vehicle.receive_date },
        { label: t("cd.containerOpened"), date: vehicle.container_open_date },
    ] : [];

    if (loading) {
        return (
            <div className="cd-page">
                <Skeleton variant="rounded" height={70} sx={{ mb: 2, borderRadius: "14px" }} />
                <div className="cd-grid">
                    <Skeleton variant="rounded" height={420} sx={{ borderRadius: "14px" }} />
                    <Skeleton variant="rounded" height={420} sx={{ borderRadius: "14px" }} />
                </div>
            </div>
        );
    }

    if (!vehicle) {
        return <div className="cd-page"><div className="cd-card cd-empty"><CarFront /> {t("cd.notFound")}</div></div>;
    }

    return (
        <div className="cd-page">
            {/* ---------------- header ---------------- */}
            <div className="cd-header cd-card">
                <div className="cd-header-left">
                    <button className="cd-back" onClick={() => (window.history.length > 1 ? window.history.back() : window.location.assign("/cars"))}>
                        <ArrowLeft />
                    </button>
                    <div>
                        <h2 className="cd-title">{vehicle.year} {vehicle.mark} {vehicle.model}</h2>
                        <div className="cd-subline">
                            <span>{t("cd.vin")} <strong className="mono">{vehicle.vin}</strong><Copy className="cd-copy" onClick={() => copyText(vehicle.vin)} /></span>
                            <span>{t("cd.lotStock")} <strong className="mono">{vehicle.lot_number}</strong></span>
                        </div>
                    </div>
                </div>
                <div className="cd-header-badges">
                    <span className="cd-status" style={{ background: `${vehicle.status_color || "#727cf5"}22`, color: vehicle.status_color || "#727cf5" }}>
                        <GeoAlt /> {vehicle.current_status || "—"}
                    </span>
                    {payStatus && <span className={`cd-pay-badge ${payStatus.cls}`}>{payStatus.cls === "ok" && <CheckCircleFill />}{payStatus.label}</span>}
                    {vehicle.is_green == 1 && <span className="cd-pay-badge ok">{t("cd.green")}</span>}
                </div>
            </div>

            {customsFine && Number(customsFine.days_overdue) > 0 && (
                <div className="cd-card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", background: "rgba(220,53,69,0.10)", border: "1px solid rgba(220,53,69,0.35)", padding: "14px 18px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <ExclamationTriangleFill style={{ color: "#dc3545", fontSize: "22px", flex: "0 0 auto" }} />
                        <div>
                            <div style={{ fontWeight: 700, color: "#dc3545" }}>
                                {t("cd.customsFine")}{Number(customsFine.active) === 0 ? ` · ${t("cd.customsFrozen")}` : ""}
                            </div>
                            <div style={{ fontSize: "13px", opacity: 0.85 }}>
                                {t("cd.customsOverdue")}: <strong>{customsFine.days_overdue}</strong> · {t("cd.customsDeadline")}: {customsFine.clearance_deadline} · {t("cd.customsRate")}: {money(customsFine.daily_rate)}/{t("cd.perDay")}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <div style={{ fontSize: "20px", fontWeight: 800, color: "#dc3545" }}>{money(customsFine.accrued_amount)}</div>
                        {canEditCosts && <button className="btn btn-sm btn-outline-danger" onClick={waiveCustomsFine}>{t("cd.customsWaive")}</button>}
                    </div>
                </div>
            )}

            <Section icon={<TruckFront />} title={t("cd.timeline")} className="cd-timeline-card">
                <Timeline vehicle={vehicle} t={t} />
            </Section>

            <div className="cd-grid">
                {/* ---------------- gallery ---------------- */}
                <div className="cd-gallery cd-card">
                    {availableTypes.length > 0 && (
                        <div className="cd-tabs">
                            {availableTypes.map((t) => (
                                <button key={t.key} className={imagesType === t.key ? "active" : ""} onClick={() => { setImagesType(t.key); setImageIndex(0); }}>{t.label}</button>
                            ))}
                        </div>
                    )}
                    {images.length > 0 ? (
                        <>
                            <div className="cd-main-image" style={{ backgroundImage: `url('${images[imageIndex]}')` }}>
                                <button className="cd-dl" onClick={downloadImages} title={t("cd.downloadAll")}><Download /> {t("common.download")}</button>
                                {images.length > 1 && <>
                                    <button className="cd-nav left" onClick={() => setImageIndex((p) => (p === 0 ? images.length - 1 : p - 1))}><ChevronLeft /></button>
                                    <button className="cd-nav right" onClick={() => setImageIndex((p) => (p === images.length - 1 ? 0 : p + 1))}><ChevronRight /></button>
                                </>}
                                <span className="cd-counter">{imageIndex + 1} / {images.length}</span>
                            </div>
                            <div className="cd-thumbs">
                                {images.map((img, i) => (
                                    <div key={i} onClick={() => setImageIndex(i)} className={`cd-thumb${i === imageIndex ? " active" : ""}`} style={{ backgroundImage: `url('${img}')` }}></div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="cd-no-image"><ImageIcon /><span>{t("cd.noImages")}</span></div>
                    )}
                </div>

                {/* ---------------- right column ---------------- */}
                <div className="cd-right">
                    {/* financials */}
                    <Section icon={<CashCoin />} title={t("cd.financials")} className="cd-finance">
                        <div className="cd-progress-head">
                            <div><span className="cd-fin-label">{t("cd.paid")}</span><span className="cd-fin-paid">{money(pay.paid)}</span></div>
                            <div className="cd-right-align"><span className="cd-fin-label">{t("cd.remaining")}</span><span className="cd-fin-debt">{money(pay.debt)}</span></div>
                        </div>
                        <div className="cd-progress"><div className="cd-progress-fill" style={{ width: `${Math.min(100, pay.pct)}%` }}></div></div>
                        <div className="cd-progress-foot"><span>{pay.pct.toFixed(0)}{t("cd.pctPaid")}</span><span>{t("cd.total")} {money(pay.total)}</span></div>
                        <div className="cd-fin-grid">
                            <InfoRow label={t("cd.auctionPrice")} value={money(vehicle.vehicle_price)} />
                            <InfoRow label={t("cd.transportation")} value={money(vehicle.total_price)} />
                            <InfoRow label={t("cd.dealerFee")} value={money(vehicle.dealer_fee)} />
                            <InfoRow label={t("cd.containerCost")} value={money(vehicle.container_cost)} />
                            <InfoRow label={t("cd.landingCost")} value={money(vehicle.landing_cost)} />
                            {Number(vehicle.car_additional_amount) > 0 && <InfoRow label={t("cd.carAddOn")} value={money(vehicle.car_additional_amount)} />}
                            {Number(vehicle.transportation_additional_amount) > 0 && <InfoRow label={t("cd.transportAddOn")} value={money(vehicle.transportation_additional_amount)} />}
                        </div>
                    </Section>

                    {/* journey timeline */}
                    <Section icon={<TruckFront />} title={t("cd.shippingJourney")}>
                        <div className="cd-timeline">
                            {timeline.map((step, i) => {
                                const done = !!fmtDate(step.date);
                                return (
                                    <div key={i} className={`cd-step${done ? " done" : ""}`}>
                                        <div className="cd-step-dot"></div>
                                        <div className="cd-step-body">
                                            <span className="cd-step-label">{step.label}</span>
                                            <span className="cd-step-date">{fmtDate(step.date) || t("cd.pending")}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Section>
                </div>
            </div>

            {/* ---------------- detail sections ---------------- */}
            <div className="cd-grid cd-grid-3">
                <Section icon={<CarFront />} title={t("cd.vehicle")}>
                    <InfoRow label={t("cd.auction")} value={vehicle.auction} />
                    <InfoRow label={t("cd.docType")} value={vehicle.doc_type} />
                    <InfoRow label={t("cd.vehicleType")} value={vehicle.vehicle_type} />
                    <InfoRow label={t("cd.hybrid")} value={<span className="cd-yn">{vehicle.is_hybrid == 1 ? <><Lightning /> {t("common.yes")}</> : t("common.no")}</span>} />
                    <InfoRow label={t("cd.key")} value={<span className="cd-yn">{vehicle.has_key == 1 ? <><Key /> {t("common.yes")}</> : t("common.no")}</span>} />
                    <InfoRow label={t("cd.insured")} value={vehicle.is_insured == 1 ? t("common.yes") : t("common.no")} />
                    <InfoRow label={t("cd.usState")} value={vehicle.us_state} />
                    <InfoRow label={t("cd.usPort")} value={vehicle.us_port} />
                </Section>

                <Section icon={<GeoAlt />} title={t("cd.shippingContainer")}>
                    <InfoRow label={t("cd.line")} value={vehicle.line} />
                    <InfoRow label={t("cd.containerNo")} value={vehicle.container_number} copy mono />
                    <InfoRow label={t("cd.booking")} value={vehicle.booking} copy mono />
                    <InfoRow label={t("cd.boat")} value={booking ? booking.boat_name : null} />
                    <InfoRow label={t("cd.terminal")} value={booking ? booking.terminal : null} />
                    <InfoRow label={t("cd.destinationPort")} value={vehicle.destination_port} />
                    <InfoRow label={t("cd.loaded")} value={fmtDate(vehicle.container_loading_date)} />
                    <InfoRow label={t("cd.opened")} value={fmtDate(vehicle.container_open_date)} />
                    <InfoRow label={t("cd.titleReceived")} value={vehicle.title_received_date ? fmtDate(vehicle.title_received_date) : (
                        <span style={{color: vehicle.title_warning ? "#dc3545" : "#b8690a", fontWeight: 600}}>
                            {vehicle.title_warning ? `⚠ ${t("cd.titleWarning")}` : t("cd.titleMissing")}
                        </span>
                    )} />
                </Section>

                <Section icon={<PersonBadge />} title={t("cd.receiverDriver")}>
                    <InfoRow label={t("cd.buyer")} value={vehicle.buyer} />
                    <InfoRow label={t("cd.receiver")} value={vehicle.receiver_fullname} />
                    <InfoRow label={t("cd.receiverId")} value={vehicle.receiver_identity_number} mono />
                    <InfoRow label={t("cd.receiverPhone")} value={vehicle.receiver_phone} copy />
                    {vehicle.driver_fullname && <InfoRow label={t("cd.driver")} value={vehicle.driver_fullname} />}
                    {vehicle.driver_phone && <InfoRow label={t("cd.driverPhone")} value={vehicle.driver_phone} copy />}
                    {vehicle.driver_company && <InfoRow label={t("cd.driverCompany")} value={vehicle.driver_company} />}
                    {vehicle.driver_car_license_number && <InfoRow label={t("cd.towPlate")} value={vehicle.driver_car_license_number} mono />}
                </Section>

                <Section icon={<Wallet2 />} title={t("cd.costs")} className="cd-costs">
                    {costSummary && (
                        <div className="cd-cost-summary" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "8px", marginBottom: "10px" }}>
                            {COST_CATEGORIES.map((c) => (
                                <div key={c} style={{ background: "var(--surface-2, rgba(127,127,127,0.08))", borderRadius: "10px", padding: "8px 10px" }}>
                                    <div style={{ fontSize: "11px", opacity: 0.7 }}>{t(`cd.cost_${c}`)}</div>
                                    <div style={{ fontWeight: 600 }}>{money(costSummary[c])}</div>
                                </div>
                            ))}
                            <div style={{ background: "rgba(13,110,253,0.12)", color: "#0d6efd", borderRadius: "10px", padding: "8px 10px" }}>
                                <div style={{ fontSize: "11px", opacity: 0.8 }}>{t("cd.costTotal")}</div>
                                <div style={{ fontWeight: 700 }}>{money(costSummary.total)}</div>
                            </div>
                        </div>
                    )}

                    {costs.length === 0 ? (
                        <div style={{ opacity: 0.6, fontSize: "13px" }}>{t("cd.costNone")}</div>
                    ) : (
                        <div className="cd-cost-list">
                            {costs.map((c) => (
                                <div key={c.id} className="cd-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                                    <span className="cd-row-label" style={{ flex: "0 0 auto" }}>
                                        <span style={{ display: "inline-block", minWidth: "90px" }}>{t(`cd.cost_${c.category}`) || c.category}</span>
                                        {c.description ? <span style={{ opacity: 0.7 }}> · {c.description}</span> : null}
                                        {c.cost_date ? <span style={{ opacity: 0.5, fontSize: "12px" }}> · {fmtDate(c.cost_date)}</span> : null}
                                    </span>
                                    <span className="cd-row-value" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <strong>{money(c.amount)}</strong>
                                        {canEditCosts && <Trash style={{ cursor: "pointer", color: "#dc3545" }} onClick={() => deleteCost(c.id)} />}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {canEditCosts && <CostForm t={t} onAdd={addCost} />}
                </Section>

                <Section icon={<Tag />} title={t("cd.discounts")} className="cd-discounts">
                    {discountTotal > 0 && (
                        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                            <div style={{ background: "rgba(52,195,143,0.12)", color: "#34c38f", borderRadius: "10px", padding: "8px 12px" }}>
                                <div style={{ fontSize: "11px", opacity: 0.8 }}>{t("cd.discountTotal")}</div>
                                <div style={{ fontWeight: 700 }}>{money(discountTotal)}</div>
                            </div>
                        </div>
                    )}

                    {discounts.length === 0 ? (
                        <div style={{ opacity: 0.6, fontSize: "13px" }}>{t("cd.discountNone")}</div>
                    ) : (
                        <div className="cd-cost-list">
                            {discounts.map((d) => (
                                <div key={d.id} className="cd-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                                    <span className="cd-row-label" style={{ flex: "0 0 auto" }}>
                                        <span style={{ opacity: 0.85 }}>{d.reason || t("cd.discount")}</span>
                                        {d.created_at ? <span style={{ opacity: 0.5, fontSize: "12px" }}> · {fmtDate(d.created_at)}</span> : null}
                                        {d.created_by ? <span style={{ opacity: 0.5, fontSize: "12px" }}> · {d.created_by}</span> : null}
                                    </span>
                                    <span className="cd-row-value" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <strong style={{ color: "#34c38f" }}>− {money(d.amount)}</strong>
                                        {canEditCosts && <Trash style={{ cursor: "pointer", color: "#dc3545" }} onClick={() => deleteDiscount(d.id)} />}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {canEditCosts && <DiscountForm t={t} onAdd={addDiscount} />}
                </Section>
            </div>

            {/* ---------------- notes ---------------- */}
            {notes.length > 0 && (
                <Section icon={<JournalText />} title={`${t("cd.notes")} (${notes.length})`} className="cd-notes">
                    {notes.map((n) => (
                        <div key={n.id} className="cd-note">
                            <div className="cd-note-head"><span className="cd-note-role">{n.role}</span></div>
                            <p className="cd-note-msg">{n.message}</p>
                        </div>
                    ))}
                </Section>
            )}
        </div>
    );
};

export default CarDetailsPage;
